import React, { useEffect, useMemo, useRef, useState } from "react";
import { Card } from "@/components/UI";
import { CaretDown, Trophy, Trash, PencilSimple, User } from "@phosphor-icons/react";
import { toggleWithScrollPreserve } from "@/components/VetTable";

const TR_MONTHS = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];

const semenBadge = (t) => (
  <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium"
    style={{
      background: t === "dişi" ? "#E4EEE6" : t === "erkek" ? "#F7EBCC" : "#EEECE5",
      color: t === "dişi" ? "#2D4A36" : t === "erkek" ? "#8B6914" : "#1A1D1A",
    }}>{t}</span>
);

// Tohumlama kayıtlarını Yıl → Ay kartları halinde gruplar. Her ay kartı özet + tıklayınca detay akordiyonu.
export function InseminationMonthlyCards({ items, onEdit, onDelete }) {
  const [openKey, setOpenKey] = useState(null);
  const didInit = useRef(false);

  const years = useMemo(() => {
    const byMonth = {};
    for (const x of items) {
      const d = (x.date || "").slice(0, 7);
      if (!/^\d{4}-\d{2}$/.test(d)) continue;
      if (!byMonth[d]) byMonth[d] = { key: d, records: [], total: 0, inek: 0, duve: 0, female: 0, male: 0, conv: 0, bulls: {}, techs: {} };
      const m = byMonth[d];
      const cnt = Number(x.animal_count || 1);
      m.records.push(x);
      m.total += cnt;
      if (x.animal_type === "düve") m.duve += cnt; else m.inek += cnt;
      if (x.semen_type === "dişi") m.female += cnt;
      else if (x.semen_type === "erkek") m.male += cnt;
      else m.conv += cnt;
      const bname = x.bull_name || x.bull_code;
      if (bname) m.bulls[bname] = (m.bulls[bname] || 0) + cnt;
      const tech = (x.technician || "").trim();
      if (tech) m.techs[tech] = (m.techs[tech] || 0) + cnt;
    }
    for (const k in byMonth) {
      byMonth[k].topBulls = Object.entries(byMonth[k].bulls)
        .sort((a, b) => b[1] - a[1]).slice(0, 3)
        .map(([name, count]) => ({ name, count }));
      // O ay tohumlama yapan TÜM personel, işlem sayısına göre azalan sırada (isimsiz hariç)
      byMonth[k].topTechs = Object.entries(byMonth[k].techs)
        .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0], "tr"))
        .map(([name, count]) => ({ name, count }));
      byMonth[k].records.sort((a, b) => (b.date || "").localeCompare(a.date || ""));
    }
    const byYear = {};
    Object.values(byMonth).forEach(m => {
      const y = m.key.slice(0, 4);
      (byYear[y] = byYear[y] || []).push(m);
    });
    return Object.entries(byYear)
      .sort((a, b) => b[0].localeCompare(a[0]))
      .map(([year, months]) => ({
        year,
        months: months.sort((a, b) => b.key.localeCompare(a.key)),
        total: months.reduce((s, m) => s + m.total, 0),
      }));
  }, [items]);

  // İlk yüklemede en güncel ayı otomatik aç
  useEffect(() => {
    if (!didInit.current && years.length) {
      setOpenKey(years[0].months[0].key);
      didInit.current = true;
    }
  }, [years]);

  if (!items.length) return <Card className="lg:col-span-2 text-center text-[#8a938c] py-10">Henüz tohumlama kaydı yok.</Card>;

  return (
    <div className="lg:col-span-2 space-y-5" data-testid="ins-monthly-cards">
      {years.map(({ year, months, total }) => (
        <div key={year}>
          <div className="flex items-center gap-3 mb-2.5">
            <h3 className="heading text-lg text-[#1f3326]" data-testid={`ins-year-${year}`}>{year}</h3>
            <span className="text-xs text-[#8a938c]">{total} tohumlama · {months.length} ay</span>
            <div className="flex-1 h-px bg-[#E5E2DC]" />
          </div>
          <div className="space-y-2.5">
            {months.map(m => {
              const label = `${TR_MONTHS[Number(m.key.slice(5, 7)) - 1]} ${year}`;
              const open = openKey === m.key;
              return (
                <Card key={m.key} className="!p-0 overflow-hidden" testId={`ins-month-${m.key}`}>
                  <button type="button" onClick={(e) => toggleWithScrollPreserve(e, m.key, setOpenKey)} data-testid={`ins-month-toggle-${m.key}`}
                    className="w-full text-left px-4 py-3.5 hover:bg-[#F7F6F3] transition-colors">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-bold text-[#1f3326] text-base">{label}</span>
                          <span className="tabnum text-sm font-bold text-white bg-[#2D4A36] px-2.5 py-0.5 rounded-full" data-testid={`ins-month-total-${m.key}`}>{m.total} tohumlama</span>
                        </div>
                        <div className="flex flex-wrap items-center gap-1.5 mt-2 text-xs">
                          <span className="px-2 py-1 rounded-md bg-[#EEF3EF] text-[#2D4A36] font-medium">İnek: <b className="tabnum">{m.inek}</b></span>
                          <span className="px-2 py-1 rounded-md bg-[#F2EFE8] text-[#5e675e] font-medium">Düve: <b className="tabnum">{m.duve}</b></span>
                          <span className="w-px h-4 bg-[#E5E2DC] mx-0.5" />
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-[#E4EEE6] text-[#2D4A36] font-medium"><span className="w-2 h-2 rounded-sm" style={{ background: "#2D6A4F" }} />Dişi: <b className="tabnum">{m.female}</b></span>
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-[#FBF1DE] text-[#8B6914] font-medium"><span className="w-2 h-2 rounded-sm" style={{ background: "#DDA74F" }} />Erkek: <b className="tabnum">{m.male}</b></span>
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-[#EEECE5] text-[#1A1D1A] font-medium"><span className="w-2 h-2 rounded-sm" style={{ background: "#8a938c" }} />Konv: <b className="tabnum">{m.conv}</b></span>
                        </div>
                        {m.topBulls.length > 0 && (
                          <div className="flex flex-wrap items-center gap-1.5 mt-2 text-xs text-[#5e675e]" data-testid={`ins-month-topbulls-${m.key}`}>
                            <Trophy size={13} weight="fill" className="text-[#C9A227]" />
                            {m.topBulls.map((b, i) => (
                              <span key={b.name} className="px-2 py-0.5 rounded-full bg-[#FAF8F3] border border-[#ECE8E0]">
                                {i === 0 ? "🥇 " : ""}{b.name} <b className="tabnum">({b.count})</b>
                              </span>
                            ))}
                          </div>
                        )}
                        {m.topTechs.length > 0 && (
                          <div className="flex flex-wrap items-center gap-1.5 mt-2 text-xs" data-testid={`ins-month-toptech-${m.key}`}>
                            <User size={13} weight="fill" className="text-[#2D4A36]" />
                            {m.topTechs.map(t => (
                              <span key={t.name} className="px-2 py-0.5 rounded-full bg-[#EEF3EF] border border-[#DCE6DE] font-medium text-[#2D4A36]">
                                {t.name} <b className="tabnum">({t.count})</b>
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <CaretDown size={18} weight="bold" className={`text-[#8a938c] mt-1 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
                    </div>
                  </button>
                  {open && (
                    <div className="border-t border-[#ECE8E0] overflow-x-auto" data-testid={`ins-month-detail-${m.key}`}>
                      <table className="data-table w-full text-sm">
                        <thead>
                          <tr>
                            <th>Tarih</th><th>Hayvan</th><th className="text-right">Adet</th>
                            <th>Tohum</th><th>Yöntem</th><th>Boğa</th><th>Tohumlayan</th><th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {m.records.map(x => (
                            <tr key={x.id} data-testid={`ins-detail-row-${x.id}`}>
                              <td className="tabnum whitespace-nowrap">{x.date}</td>
                              <td className="capitalize">{x.animal_type || "inek"}</td>
                              <td className="text-right tabnum font-semibold">{x.animal_count || 1}</td>
                              <td>{semenBadge(x.semen_type)}</td>
                              <td>{x.method}</td>
                              <td>{x.bull_name ? <><b>{x.bull_name}</b>{x.bull_code && <span className="text-xs text-[#8a938c]"> ({x.bull_code})</span>}</> : (x.bull_code || "—")}</td>
                              <td>{x.technician || "—"}</td>
                              <td className="text-right whitespace-nowrap">
                                <button data-testid={`ins-edit-${x.id}`} onClick={() => onEdit(x)} title="Düzenle" className="text-[#5A6E5F] hover:text-[#2D4A36] p-1"><PencilSimple size={14} /></button>
                                <button data-testid={`ins-del-${x.id}`} onClick={() => onDelete(x)} title="Sil" className="text-[#C45B45] hover:text-[#8B2E1D] p-1"><Trash size={14} /></button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
